import CollapsingDisabledMedicalQuestionDecorator from "../Components/CollapsingDisabledMedicalQuestionDecorator";
import NoElementFormItem from "../Components/NoElementFormItem";
import SideEffectMedicalQuestionDecorator from "../Components/SideEffectMedicalQuestionDecorator";
import YesNoToggleMedicalQuestion from "../Components/YesNoToggleMedicalQuestion";
import ComposableMedicalFormEvaluator, {
  IComposableMedicalFormEvaluatorConfig
} from "../Types/ComposableMedicalFormEvaluator";
import IMedicalFormElementProps from "../Types/IMedicalFormElement";
import IMedicalQuestionnaireCompleteEvaluator from "../Types/IMedicalQualificationCompleteEvaluator";
import IMedicalQualificationNonCandidateEvaluator from "../Types/IMedicalQualificationNonCandidateEvaluator";

const questionIds = {
  cancerTreatment: "radiationMedicalQuestions.cancer",
  activeCancer: "radiationMedicalQuestions.chemo",
  cancerChemo: "radiationMedicalQuestions.cancerChemo"
};

const cancerChemoQuestion: React.FC<IMedicalFormElementProps> = props =>
  YesNoToggleMedicalQuestion({
    ...props,
    questionId: questionIds.cancerChemo,
    text: "Are you undergoing chemotherapy treatments?",
    ChildFormItem: null
  });

const collapseCancerChemoDecorator: React.FC<IMedicalFormElementProps> = props =>
  CollapsingDisabledMedicalQuestionDecorator({
    ...props,
    isDisabled: formState => formState[questionIds.activeCancer] !== false,
    ChildFormItem: cancerChemoQuestion
  });

const activeCancerQuestion: React.FC<IMedicalFormElementProps> = props =>
  YesNoToggleMedicalQuestion({
    ...props,
    questionId: questionIds.activeCancer,
    text: "Do you have active cancer?",
    ChildFormItem: collapseCancerChemoDecorator
  });

const cancerDialogue: React.FC<IMedicalFormElementProps> = props =>
  NoElementFormItem({
    ...props,
    questionId: "radiationMedicalQuestions.cancerDialogue",
    text: "Thank you for sharing. We are unable to work on any areas of the body previously treated with radiation.",
    ChildFormItem: activeCancerQuestion
  });

const collapseCancerDialogueDecorator: React.FC<IMedicalFormElementProps> = props =>
  CollapsingDisabledMedicalQuestionDecorator({
    ...props,
    isDisabled: formState => formState[questionIds.cancerTreatment] !== true,
    ChildFormItem: cancerDialogue
  });

const cancerTreatmentQuestion: React.FC<IMedicalFormElementProps> = props =>
  YesNoToggleMedicalQuestion({
    ...props,
    questionId: questionIds.cancerTreatment,
    text: "Have you been treated for cancer?",
    ChildFormItem: collapseCancerDialogueDecorator
  });

const sideEffectsRadiationQuestion: React.FC<IMedicalFormElementProps> = props =>
  SideEffectMedicalQuestionDecorator({
    ...props,
    transformMedicalConclusions: (previousConclusions, formState) => ({
      ...previousConclusions,
      hasRadiationProtectedAreas: formState[questionIds.cancerTreatment] === true
    }),
    transformMedicalFormState: oldState => {
      let newState = { ...oldState };
      if (newState[questionIds.cancerTreatment] === false)
        newState = { ...newState, [questionIds.activeCancer]: null, [questionIds.cancerChemo]: null };
      if (newState[questionIds.activeCancer] === false) newState[questionIds.cancerChemo] = null;
      return newState;
    },
    ChildFormItem: cancerTreatmentQuestion
  });

export const RadiationFormEvaluatorConfig: IComposableMedicalFormEvaluatorConfig = {
  allFormQuestionIds: Object.values(questionIds),
  requiredQuestionPredicates: {
    [questionIds.activeCancer]: form => form[questionIds.cancerTreatment] === true,
    [questionIds.cancerChemo]: form => form[questionIds.activeCancer] === false
  },
  nonCandidateIgnoredQuestionIds: [questionIds.cancerTreatment],
  nonCandidateQuestionPredicates: null
};

export const RadiationFormEvaluator: IMedicalQuestionnaireCompleteEvaluator &
  IMedicalQualificationNonCandidateEvaluator = new ComposableMedicalFormEvaluator(RadiationFormEvaluatorConfig);

export const radiationFormQuestions: React.FC<IMedicalFormElementProps>[] = [sideEffectsRadiationQuestion];
